/**
 * TKP-119 — görev durumu/önceliği organizasyon bazında kayıt: saf yardımcılar (store/Vue yok).
 * Kayıtlar yüklenmeden ya da eski organizasyonda (tohum yok) sistem varsayılanları kullanılır — anahtarlar eski enum ile
 * birebir, böylece rozet/seçici/kanban bugünkü gibi çalışır.
 */
export const STATUS_KINDS = ["OPEN", "DONE", "REJECTED"];

/** Sistem varsayılanları — ad yerelleştirme anahtarıyla (`t` verilince çevrilir), sıra bugünkü kanban sırası. */
export const defaultStatuses = (t = (k) => k) => [
  { key: "TODO", kind: "OPEN", order: 1, color: "#FF7C48", system: true, name: t("task.status.todo") },
  { key: "IN_PROGRESS", kind: "OPEN", order: 2, color: "#2998FF", system: true, name: t("task.status.in-progress") },
  { key: "APPROVAL", kind: "OPEN", order: 3, color: "#9440FF", system: true, name: t("task.status.approval") },
  { key: "DONE", kind: "DONE", order: 4, color: "#2ECD06", system: true, name: t("task.status.done") },
  { key: "NOT_FIT", kind: "REJECTED", order: 5, color: "red", system: true, name: t("task.status.not-fit") },
  { key: "UNWORKED", kind: "OPEN", order: 6, color: "gray", system: true, name: t("task.status.unworked") },
];
export const defaultPriorities = (t = (k) => k) => [
  { value: 1, order: 1, color: "#458bee", system: true, name: t("task.priority.low") },
  { value: 2, order: 2, color: "#ffc148", system: true, name: t("task.priority.medium") },
  { value: 3, order: 3, color: "rgb(230, 107, 107)", system: true, name: t("task.priority.high") },
  { value: 4, order: 4, color: "red", system: true, name: t("task.priority.highest") },
];

const localized = (name, language) => (name && typeof name === "object" ? name[language] || name.tr || name.en || Object.values(name).find(Boolean) || "" : name || "");
const byOrder = (a, b) => (a.order || 0) - (b.order || 0);

/** Kayıt listesi (store) doluysa onu, değilse varsayılanları döner; adlar dile çözülür, sıraya dizilir. */
export const resolveStatuses = (records, language, t) => (records && records.length ? [...records] : defaultStatuses(t)).map((s) => ({ ...s, label: localized(s.name, language) })).sort(byOrder);
export const resolvePriorities = (records, language, t) => (records && records.length ? [...records] : defaultPriorities(t)).map((p) => ({ ...p, label: localized(p.name, language) })).sort(byOrder);

/** Anahtarın kaydı; bilinmeyen anahtar için etiket = anahtar, renk gri (ekran kırılmasın). */
export const statusOf = (statuses, key) => statuses.find((s) => s.key === String(key)) || { key: String(key || ""), label: String(key || ""), color: "#6c757d", kind: "OPEN", unknown: true };
export const priorityOf = (priorities, value) => priorities.find((p) => String(p.value) === String(value)) || priorities.find((p) => p.value === 2) || { value: 2, label: "", color: "#ffc148", unknown: true };

/** Kanban sütunları: her durum bir sütun (sırayla); görevler anahtara göre dağıtılır, bilinmeyen anahtar ek sütun olur. */
export const kanbanColumns = (statuses, tasks = []) => {
  const columns = statuses.map((s) => ({ name: s.label, definition: s.key, dotColor: s.color, items: [] }));
  for (const task of tasks) {
    let column = columns.find((c) => c.definition === task.status);
    if (!column) { column = { name: String(task.status), definition: String(task.status), dotColor: "#6c757d", items: [] }; columns.push(column); }
    column.items.push(task);
  }
  return columns;
};

/** Seçici seçenekleri (tekil seçim bileşenleri `definition` + `name{lang}` bekliyor). */
export const statusOptions = (statuses, language) => statuses.map((s) => ({ definition: s.key, name: { [language]: s.label }, color: s.color, kind: s.kind }));
export const priorityOptions = (priorities, language) => priorities.map((p) => ({ definition: String(p.value), name: { [language]: p.label }, color: p.color }));

/** Ayarlar ekranı: kayıt düzenlenebilir mi (sistem kaydında anahtar/sınıf/değer kilitli, ad ve renk açık). */
export const editableFields = (record) => (record && record.system ? ["name", "color", "order"] : ["name", "color", "order", "kind"]);

/** TKP-168 — organizasyon kategorileri: ada (her dil) göre süzgeç, sıraya dizili; seçiciler `{_id, name}` bekler. */
export const resolveCategories = (records, language) => (records || []).map((c) => ({ ...c, label: localized(c.name, language) })).sort(byOrder);
// Türkçe küçük harf (İ→i) + ı→i eşitlemesi: "TRAIN" ↔ "Training" ve "İÇ" ↔ "İç" aynı ölçekte karşılaştırılır.
const fold = (text) => String(text || "").toLocaleLowerCase("tr").replace(/ı/g, "i");
export const searchCategories = (records, value) => {
  const needle = fold(value).trim();
  return [...(records || [])].sort(byOrder).filter((c) => !needle || Object.values(c.name || {}).some((n) => fold(n).includes(needle)));
};

/** TKP-176 md.51 — silinemeyen sistem durumları (backend PROTECTED_STATUS_KEYS ile aynı): DONE (tamamlanma akışları, raporlar) ve TODO (yeni görev varsayılanı). */
export const PROTECTED_STATUS_KEYS = ["DONE", "TODO"];
export const isDeletableStatus = (record) => !(record && record.system && PROTECTED_STATUS_KEYS.includes(record.key));

// ---- TKP-220 — e-posta ayarı (goreveposta.pdf) ve görev tipleri
export const MAIL_EVENTS = ["ASSIGNED", "ASSIGNEE_CHANGED", "STATUS_CHANGED", "DUEDATE_CHANGED", "PRIORITY_CHANGED", "COMMENT_ADDED", "FILE_UPLOADED", "TASK_EDITED"];
export const MAIL_NONE = "NONE";
export const MAIL_RECIPIENTS = ["ASSIGNEE", "REPORTER"];
export const MAIL_TEMPLATE_KEYS = [...MAIL_EVENTS, "DUE_REMINDER", "OVERDUE_REMINDER"];
export const MAIL_VARIABLES = ["recipient", "taskNo", "taskName", "actor", "change", "days", "duedate"]; // TKP-239 g.9: {link} kalktı — bağlantı e-postanın "buraya tıklayınız" satırında, doğrudan göreve

/** Ayar formu: sunucudaki ayar (yoksa "Yok") → düzenlenebilir kopya. */
export const mailForm = (mail) => ({
  events: [...((mail && mail.events) || [])],
  recipients: [...((mail && mail.recipients) || [])],
  dueReminder: { enabled: !!(mail && mail.dueReminder && mail.dueReminder.enabled), days: [...((mail && mail.dueReminder && mail.dueReminder.days) || [])] },
  overdueReminder: { enabled: !!(mail && mail.overdueReminder && mail.overdueReminder.enabled), days: [...((mail && mail.overdueReminder && mail.overdueReminder.days) || [])] },
  templates: JSON.parse(JSON.stringify((mail && mail.templates) || {})),
});

/**
 * Olay seçimi: "Yok" diğerleriyle birlikte seçilemez — son seçilen "Yok" ise yalnız o (boş liste), başka bir olay seçilince "Yok" düşer.
 * Dönüş: kaydedilecek olay listesi (Yok = []).
 */
export const pickEvents = (previous = [], next = []) => {
  const added = next.filter((e) => !previous.includes(e));
  if (added.includes(MAIL_NONE)) return [];
  return next.filter((e) => e !== MAIL_NONE && MAIL_EVENTS.includes(e));
};

/** Alıcı alanı görünür ve zorunlu mu (olay seçili ya da bir hatırlatma açık). */
export const needsRecipients = (form) => !!form && (form.events.length > 0 || form.dueReminder.enabled || form.overdueReminder.enabled);

/** Gün girişi: "3, 5 7" ya da dizi → tekrarsız, 1–365 tam sayılar, büyükten küçüğe. */
export const parseDays = (value) =>
  Array.from(new Set((Array.isArray(value) ? value : String(value || "").split(/[^0-9]+/)).map((d) => Number(d)).filter((d) => Number.isInteger(d) && d >= 1 && d <= 365))).sort((a, b) => b - a);

/** İstemci tarafı ön doğrulama (sunucu da doğrular): hata anahtarı ya da null. */
export const mailFormError = (form) => {
  if (needsRecipients(form) && !form.recipients.length) return "task.settings.mail.recipients-required";
  for (const r of [form.dueReminder, form.overdueReminder]) if (r.enabled && !r.days.length) return "task.settings.mail.days-required";
  return null;
};

/** Kaydedilecek yük: boş metinler düşer. */
export const mailPayload = (form) => {
  const templates = {};
  for (const key of MAIL_TEMPLATE_KEYS) {
    const t = form.templates[key] || {};
    const subject = String(t.subject || "").trim();
    const body = String(t.body || "").trim();
    if (subject || body) templates[key] = { ...(subject ? { subject } : {}), ...(body ? { body } : {}) };
  }
  return { events: form.events, recipients: form.recipients, dueReminder: form.dueReminder, overdueReminder: form.overdueReminder, templates };
};

export const resolveTypes = (records, language) => resolveCategories(records, language);
